// src/components/PolicyMarkdown.tsx
// A tiny, dependency-free, XSS-safe Markdown-subset renderer for policy content.
// Strategy: HTML-escape the raw input FIRST, then emit only our own controlled
// tags. No untrusted HTML ever reaches the DOM. Link hrefs are scheme-checked.
import React, { useMemo } from 'react';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeHref(raw: string): string | null {
  const u = raw.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (/^mailto:/i.test(u)) return u;
  if (/^\//.test(u)) return u; // in-app relative link
  return null; // reject javascript:, data:, etc.
}

// Inline formatting on an already-escaped string.
function inline(text: string): string {
  let out = text;
  // links: [label](url)
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, url) => {
    const href = safeHref(url);
    if (!href) return label;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  // inline code: `code`
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  // bold: **text** (before italic so single * doesn't grab it)
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // italic: *text* or _text_
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  return out;
}

function render(markdown: string): string {
  const lines = escapeHtml(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  let list: { type: 'ul' | 'ol'; items: string[] } | null = null;
  let para: string[] = [];

  const flushList = () => {
    if (!list) return;
    const inner = list.items.map((i) => `<li>${inline(i)}</li>`).join('');
    html.push(`<${list.type}>${inner}</${list.type}>`);
    list = null;
  };
  const flushPara = () => {
    if (!para.length) return;
    html.push(`<p>${inline(para.join('<br/>'))}</p>`);
    para = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) {
      flushList();
      flushPara();
      continue;
    }
    const h3 = line.match(/^###\s+(.*)$/);
    const h2 = line.match(/^##\s+(.*)$/);
    const h1 = line.match(/^#\s+(.*)$/);
    const ul = line.match(/^[-*]\s+(.*)$/);
    const ol = line.match(/^\d+\.\s+(.*)$/);

    if (h3 || h2 || h1) {
      flushList();
      flushPara();
      const tag = h3 ? 'h3' : 'h2';
      const txt = (h3 || h2 || h1)![1];
      html.push(`<${tag}>${inline(txt)}</${tag}>`);
    } else if (ul) {
      flushPara();
      if (!list || list.type !== 'ul') {
        flushList();
        list = { type: 'ul', items: [] };
      }
      list.items.push(ul[1]);
    } else if (ol) {
      flushPara();
      if (!list || list.type !== 'ol') {
        flushList();
        list = { type: 'ol', items: [] };
      }
      list.items.push(ol[1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushList();
  flushPara();
  return html.join('');
}

export function PolicyMarkdown({ content }: { content: string }) {
  const __html = useMemo(() => render(content), [content]);
  return (
    <div className="cp-md">
      <div dangerouslySetInnerHTML={{ __html }} />
      <style>{`
        .cp-md { color: #4a4a4a; font-size: 14.5px; line-height: 1.75; }
        .cp-md > div > :first-child { margin-top: 0; }
        .cp-md h2 { font-size: 18px; font-weight: 600; color: #0f2d1f; margin: 1.6em 0 .5em; letter-spacing: -0.01em; }
        .cp-md h3 { font-size: 15px; font-weight: 600; color: #1a1a1a; margin: 1.3em 0 .4em; }
        .cp-md p { margin: 0 0 1em; }
        .cp-md ul, .cp-md ol { margin: 0 0 1em; padding-left: 1.4em; }
        .cp-md ul { list-style: disc; }
        .cp-md ol { list-style: decimal; }
        .cp-md li { margin: .3em 0; padding-left: .2em; display: list-item; }
        .cp-md ul li::marker { color: #1f4030; }
        .cp-md ol li::marker { color: #1f4030; font-weight: 600; }
        .cp-md strong { font-weight: 600; color: #1a1a1a; }
        .cp-md em { font-style: italic; }
        .cp-md a { color: #0f2d1f; text-decoration: underline; text-underline-offset: 2px; }
        .cp-md a:hover { color: #1f4030; }
        .cp-md code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.88em; background: #f4f1ea; border: 1px solid #e8e3d8; padding: 1px 5px; border-radius: 5px; }
      `}</style>
    </div>
  );
}

export default PolicyMarkdown;
